import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  CreditCard, DollarSign, Users, ArrowLeft, Plus, Edit, Trash2,
  TrendingUp, Wallet, PiggyBank, Building, ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PayrollStructuresPage = () => {
  const [structures, setStructures] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: "",
    basic_salary: "",
    housing_allowance: "",
    transport_allowance: "",
    medical_allowance: "",
    pension_contribution: "",
    tax_rate: "0.25"
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [structuresRes, employeesRes] = await Promise.all([
          axios.get(`${API}/admin/payroll/structures`, { headers }),
          axios.get(`${API}/admin/employees`, { headers })
        ]);
        setStructures(structuresRes.data.structures || []);
        setEmployees(employeesRes.data.employees || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/payroll/structures`, {
        ...formData,
        basic_salary: parseFloat(formData.basic_salary),
        housing_allowance: parseFloat(formData.housing_allowance || 0),
        transport_allowance: parseFloat(formData.transport_allowance || 0),
        medical_allowance: parseFloat(formData.medical_allowance || 0),
        pension_contribution: parseFloat(formData.pension_contribution || 0),
        tax_rate: parseFloat(formData.tax_rate)
      }, { headers });
      toast.success("Salary structure created");
      setShowForm(false);
      // Refresh
      const res = await axios.get(`${API}/admin/payroll/structures`, { headers });
      setStructures(res.data.structures || []);
    } catch (error) {
      toast.error("Failed to create structure");
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(val);
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.full_name || "Unknown";
  };

  const totalPayroll = structures.reduce((sum, s) => sum + (s.gross_salary || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Payroll...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="payroll-structures-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Salary Structures
            </h1>
            <p className="text-slate-400 text-sm">Manage employee compensation packages</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Structure
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{structures.length}</p>
            <p className="text-xs text-slate-400">Total Structures</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Wallet className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{formatCurrency(totalPayroll)}</p>
            <p className="text-xs text-slate-400">Monthly Payroll</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <TrendingUp className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{formatCurrency(totalPayroll / (structures.length || 1))}</p>
            <p className="text-xs text-slate-400">Avg Salary</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <PiggyBank className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-purple-400">
              {formatCurrency(structures.reduce((sum, s) => sum + (s.pension_contribution || 0), 0))}
            </p>
            <p className="text-xs text-slate-400">Total Pension</p>
          </CardContent>
        </Card>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-white">Create Salary Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Employee</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name} - {emp.department}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Basic Salary ($)</label>
                    <Input
                      type="number"
                      value={formData.basic_salary}
                      onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Housing Allowance ($)</label>
                    <Input
                      type="number"
                      value={formData.housing_allowance}
                      onChange={(e) => setFormData({ ...formData, housing_allowance: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Transport Allowance ($)</label>
                    <Input
                      type="number"
                      value={formData.transport_allowance}
                      onChange={(e) => setFormData({ ...formData, transport_allowance: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Medical Allowance ($)</label>
                    <Input
                      type="number"
                      value={formData.medical_allowance}
                      onChange={(e) => setFormData({ ...formData, medical_allowance: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Pension Contribution ($)</label>
                    <Input
                      type="number"
                      value={formData.pension_contribution}
                      onChange={(e) => setFormData({ ...formData, pension_contribution: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Tax Rate</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Create</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Structures Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">All Salary Structures</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Basic Salary</th>
                  <th>Allowances</th>
                  <th>Gross</th>
                  <th>Pension</th>
                  <th>Tax Rate</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {structures.map((structure) => (
                  <tr key={structure.id}>
                    <td className="text-white font-medium">{getEmployeeName(structure.employee_id)}</td>
                    <td className="text-[#c9a227]">{formatCurrency(structure.basic_salary)}</td>
                    <td className="text-slate-300">
                      {formatCurrency((structure.housing_allowance || 0) + (structure.transport_allowance || 0) + (structure.medical_allowance || 0))}
                    </td>
                    <td className="text-green-400 font-semibold">{formatCurrency(structure.gross_salary)}</td>
                    <td className="text-purple-400">{formatCurrency(structure.pension_contribution)}</td>
                    <td className="text-slate-300">{((structure.tax_rate || 0) * 100).toFixed(0)}%</td>
                    <td>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-blue-400">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollStructuresPage;
